import { AppData } from '../AppContext';
import templates from '../templates';
import { getBreite, getHoehe, getLaenge, getTiefe, getWandschienenhoehe } from './app-data';

export interface ValidationMessage {
  severity: Severity;
  messageContent: any;
  kEigenschaft: number[];
}

function evaluateConditionElement(condition: ConditionElement, appData: AppData): boolean {
  const value = appData.dynamic.selectedVariationWerte[condition.kEigenschaft];
  if (condition.operator === 'equals') {
    return value === condition.value;
  }
  if (condition.operator === 'greaterThan') {
    return value > condition.value;
  }
  if (condition.operator === 'lessThan') {
    return value < condition.value;
  }
  throw new Error(`Unbehandelter Operator in Condition: ${condition.operator}`);
}

function evaluateCondition(condition: Condition, appData: AppData): boolean {
  return condition.and.filter((c) => evaluateConditionElement(c, appData)).length === condition.and.length;
}

function matchingCondition(
  condition: Condition | undefined,
  appData: AppData
): boolean {
  if (!condition) {
    return true;
  }
  return evaluateCondition(condition, appData);
}

export const validateRolladen = (appData: AppData): ValidationMessage[] => {
  const validations: ValidationMessage[] = [];
  const breite = getBreite(appData);
  const hoehe = getHoehe(appData);
  appData.static.config.validations.forEach((validation) => {
    if (!validation.combiner) {
      if (validation.kEigenschaft.length === 1) {
        const kEigenschaft = validation.kEigenschaft[0];
        let value: number | undefined;
        if (kEigenschaft === appData.static.kEigenschaftBreite) {
          value = breite;
        } else if (kEigenschaft === appData.static.kEigenschaftHoehe) {
          value = hoehe;
        }
        if (value !== undefined && matchingCondition(validation.condition, appData)) {
          if (validation.operator === 'greaterThan') {
            if (value > (validation.value as number)) {
              validations.push({
                kEigenschaft: [kEigenschaft],
                severity: validation.severity,
                messageContent: templates(appData.static.artikel, validation.validationTemplateParameter ?? validation.value)[validation.validationTemplateId],
              });
            }
          } else if (validation.operator === 'lessThan') {
            if (value < (validation.value as number)) {
              validations.push({
                kEigenschaft: [kEigenschaft],
                severity: validation.severity,
                messageContent: templates(appData.static.artikel, validation.validationTemplateParameter ?? validation.value)[validation.validationTemplateId],
              });
            }
          } else {
            throw new Error(`Unbehandelter Operator: ${validation.operator}`);
          }
        }
      } else {
        throw new Error('Ohne combiner ist nur eine kEigenschaft möglich');
      }
    } else if (validation.combiner === 'multiply') {
      if (validation.kEigenschaft.length === 2) {
        const kEigenschaft1 = validation.kEigenschaft[0];
        const kEigenschaft2 = validation.kEigenschaft[1];
        let value1: number | undefined;
        if (kEigenschaft1 === appData.static.kEigenschaftBreite) {
          value1 = breite;
        } else if (kEigenschaft1 === appData.static.kEigenschaftHoehe) {
          value1 = hoehe;
        }
        let value2: number | undefined;
        if (kEigenschaft2 === appData.static.kEigenschaftBreite) {
          value2 = breite;
        } else if (kEigenschaft2 === appData.static.kEigenschaftHoehe) {
          value2 = hoehe;
        }
        if (value1 !== undefined && value2 !== undefined) {
          if (validation.operator === 'greaterThan') {
            if (value1 * value2 > (validation.value as number)) {
              validations.push({
                kEigenschaft: [kEigenschaft1, kEigenschaft2],
                severity: validation.severity,
                messageContent: templates(appData.static.artikel, validation.validationTemplateParameter ?? validation.value)[validation.validationTemplateId],
              });
            }
          } else if (validation.operator === 'lessThan') {
            if (value1 * value2 < (validation.value as number)) {
              validations.push({
                kEigenschaft: [kEigenschaft1, kEigenschaft2],
                severity: validation.severity,
                messageContent: templates(appData.static.artikel, validation.validationTemplateParameter ?? validation.value)[validation.validationTemplateId],
              });
            }
          } else {
            throw new Error(`Unbehandelter Operator: ${validation.operator}`);
          }
        }
      } else {
        throw new Error(`Mit combiner ${validation.combiner} sind zwei kEigenschaft nötig`);
      }
    } else {
      throw new Error(`Unbekannter combiner: ${validation.combiner}`);
    }
  });
  return validations;
};

const validateSingleValue = (
  validation: Validation,
  value: number,
  kEigenschaftToValidate: number,
  artikel: Artikel
): ValidationMessage[] => {
  const validations: ValidationMessage[] = [];
  if (!validation.combiner) {
    if (validation.kEigenschaft.length === 1) {
      const kEigenschaft = validation.kEigenschaft[0];
      if (kEigenschaft === kEigenschaftToValidate) {
        if (value !== undefined) {
          if (validation.operator === 'greaterThan') {
            if (value > (validation.value as number)) {
              validations.push({
                kEigenschaft: [kEigenschaft],
                severity: validation.severity,
                messageContent: templates(artikel, validation.validationTemplateParameter ?? validation.value)[validation.validationTemplateId],
              });
            }
          } else if (validation.operator === 'lessThan') {
            if (value < (validation.value as number)) {
              validations.push({
                kEigenschaft: [kEigenschaft],
                severity: validation.severity,
                messageContent: templates(artikel, validation.validationTemplateParameter ?? validation.value)[validation.validationTemplateId],
              });
            }
          } else {
            throw new Error(`Unbehandelter Operator: ${validation.operator}`);
          }
        }
      }
    } else {
      throw new Error('Ohne combiner ist nur eine kEigenschaft möglich');
    }
  } else {
    throw new Error(`Unbekannter oder nicht unterstützter combiner: ${validation.combiner}`);
  }
  return validations;
};

export const validateLaenge = (appData: AppData): ValidationMessage[] => {
  const validations: ValidationMessage[] = [];
  const laenge = getLaenge(appData);
  appData.static.config.validations.forEach((validation) => {
    if (matchingCondition(validation.condition, appData)) {
      validations.push(...validateSingleValue(validation, laenge, appData.static.kEigenschaftLaenge, appData.static.artikel));
    }
  });
  return validations;
};

export const validateBreite = (appData: AppData): ValidationMessage[] => {
  const validations: ValidationMessage[] = [];
  const breite = getBreite(appData);
  appData.static.config.validations.forEach((validation) => {
    if (matchingCondition(validation.condition, appData)) {
      validations.push(...validateSingleValue(validation, breite, appData.static.kEigenschaftBreite, appData.static.artikel));
    }
  });
  return validations;
};

export const validateTiefe = (appData: AppData): ValidationMessage[] => {
  const validations: ValidationMessage[] = [];
  const tiefe = getTiefe(appData);
  appData.static.config.validations.forEach((validation) => {
    if (matchingCondition(validation.condition, appData)) {
      validations.push(...validateSingleValue(validation, tiefe, appData.static.kEigenschaftTiefe, appData.static.artikel));
    }
  });
  return validations;
};

export const validateWandschienenhoehe = (appData: AppData): ValidationMessage[] => {
  const validations: ValidationMessage[] = [];
  const { artikel, kEigenschaftWandschienenhoehe, kEigenschaftStauhoehe } = appData.static;
  const kEigenschaftWertHoehe = appData.dynamic.selectedVariationWerte[kEigenschaftStauhoehe];
  const wandschienenhoehe = getWandschienenhoehe(appData);
  appData.static.config.validations.forEach((validation) => {
    if (!validation.combiner) {
      if (validation.kEigenschaft.length === 1) {
        if (validation.kEigenschaft[0] === kEigenschaftWandschienenhoehe) {
          if (validation.operator === 'greaterThan') {
            if (wandschienenhoehe > (validation.value as number)) {
              validations.push({
                kEigenschaft: [kEigenschaftWandschienenhoehe],
                severity: validation.severity,
                messageContent: templates(artikel, validation.validationTemplateParameter ?? validation.value)[validation.validationTemplateId],
              });
            }
          } else if (validation.operator === 'lessThan') {
            if (wandschienenhoehe < (validation.value as number)) {
              validations.push({
                kEigenschaft: [kEigenschaftWandschienenhoehe],
                severity: validation.severity,
                messageContent: templates(artikel, validation.validationTemplateParameter ?? validation.value)[validation.validationTemplateId],
              });
            }
          } else if (validation.operator === 'lessThanDependentValue') {
            const dependentValue = validation.value as DependentValue;
            const value = dependentValue[kEigenschaftStauhoehe][kEigenschaftWertHoehe];
            if (wandschienenhoehe < value) {
              validations.push({
                kEigenschaft: [kEigenschaftWandschienenhoehe],
                severity: validation.severity,
                messageContent: templates(artikel, value)[validation.validationTemplateId],
              });
            }
          } else {
            throw new Error(`Unbehandelter Operator: ${validation.operator}`);
          }
        }
      } else {
        throw new Error('Ohne combiner ist nur eine kEigenschaft möglich');
      }
    } else {
      throw new Error(`Unbekannter oder nicht unterstützter combiner: ${validation.combiner}`);
    }
  });
  return validations;
};
